import { computed, onMounted, onBeforeUnmount, useHost, shallowRef } from "vue";
import { useSharedState } from "composables/useSharedState";
import { closestAcrossShadowBoundaries } from "@/runtime/shared/shadow-dom";
import { mergeClassProp } from "@/runtime/style-engine/define-variants";
import { getChatScroll, registerChatGroup, unregisterChatGroup } from "./useChat";
import type { ChatGroupProps } from "./chat.types";
import { chatGroupClasses, chatGroupContentClasses, chatGroupMetaClasses, chatAvatarClasses } from "./chat.styles";

export interface UseChatGroupOptions {
  props: Readonly<ChatGroupProps>;
}

// Return type inferred — avoids WritableComputedRef mismatch with Vue's computed().

type ChatGroupState = ReturnType<typeof useSharedState<ChatGroupProps>>;

/** Compute side from `from` vs the parent e-chat's current-user attribute. */
const buildChatGroupSide = (state: ChatGroupState, props: Readonly<ChatGroupProps>, currentUser: string) =>
  computed<"left" | "right">(() => {
    const from = (state.from?.value as string | undefined) ?? props.from;
    return from && from === currentUser ? "right" : "left";
  });

/** Watch visibility and emit `chat:group-visible` while observed. */
const setupGroupVisibilityObserver = (
  host: Element,
  scrollRoot: HTMLElement | null,
  props: Readonly<ChatGroupProps>,
): void => {
  const observer = new IntersectionObserver(
    (entries) => {
      if (entries[0].isIntersecting) {
        host.dispatchEvent(
          new CustomEvent("chat:group-visible", {
            detail: { from: props.from, sentAt: props.sentAt },
            bubbles: true,
            composed: true,
          }),
        );
      }
    },
    { root: scrollRoot },
  );
  observer.observe(host);
  onBeforeUnmount(() => {
    observer.disconnect();
    unregisterChatGroup(host);
  });
};

export const useChatGroup = (options: UseChatGroupOptions) => {
  const { props } = options;
  const host = useHost();
  const state = useSharedState(props);

  const cachedChatHost = shallowRef<Element | null>(null);
  if (host) cachedChatHost.value = closestAcrossShadowBoundaries(host, "e-chat");
  const currentUser = cachedChatHost.value?.getAttribute("current-user") ?? "receiver";
  const avatarEnabled = cachedChatHost.value?.getAttribute("show-avatar") !== "false";

  const side = buildChatGroupSide(state, props, currentUser);
  if (host) registerChatGroup(host, side.value);

  const classes = computed(() => mergeClassProp(chatGroupClasses({ side: side.value }), props.class));
  const contentClasses = computed(() => chatGroupContentClasses({ side: side.value }));
  const metaClasses = chatGroupMetaClasses({});
  const avatarClasses = chatAvatarClasses({});

  onMounted(() => {
    if (!host) return;
    const chatHost = cachedChatHost.value;
    const scrollRoot = chatHost ? (getChatScroll(chatHost) ?? null) : null;
    setupGroupVisibilityObserver(host, scrollRoot, props);
  });

  const showAvatar = computed(() => avatarEnabled && side.value === "left");
  const showFrom = computed(() => !!((state.from?.value as string | undefined) ?? props.from) && side.value === "left");
  const initials = computed(() => {
    const from = (state.from?.value as string | undefined) ?? props.from;
    return from ? from.charAt(0).toUpperCase() : "";
  });

  return { classes, contentClasses, metaClasses, avatarClasses, side, showAvatar, showFrom, initials };
};
